import type { AppState } from 'src/reducers';
import { createSelector, type Selector } from 'reselect';
import { flattenAndLabelGroup } from 'src/components/StandardCardView/SortAndGroup';
import type { SubheaderSlice } from 'src/components/Subheader/Subheader.slice';
import type { CardViewDataInputs, CardViewOptionalInputs } from 'src/components/StandardCardView/UIData.types';
import type { FlowTypeSlice } from './FlowType.slice';
import type { TreePivot, WorklistInfo } from 'src/worker/pivotWorker.types';
import { externalGridSearchFields } from 'src/utils/Domain/Constants';
import { toSummaries } from 'src/utils/Pivot/RollUp';
import type { GetSummaryOutput } from 'src/pages/Hindsighting/StyleColorReview/CollectionView/CollectionView.selectors';
import { filterData } from 'src/utils/Pivot/Filter';
import { generateGroupBy } from '../TopTYvsLY/TopTYvsLY.container';
import { isNil } from 'lodash';
import { getGroupBySelectedOptionProperty } from 'src/utils/Pivot/Sort';
import processDataForCardView from 'src/components/StandardCardView/CardViewDataProcessor';
import { getDataFromCache } from 'src/services/pivotServiceCache';
import { TenantConfigViewData } from 'src/dao/tenantConfigClient';

const getTreeData: Selector<AppState, TreePivot> = (state) => {
  return getDataFromCache(state.pages.hindsighting.styleColorReview.flowType)?.tree || [];
};

const getRollup: Selector<AppState, TenantConfigViewData> = (state) => {
  return state.pages.hindsighting.styleColorReview.flowType.calcViewDefn;
};
const getView: Selector<AppState, FlowTypeSlice> = (state) => {
  return state.pages.hindsighting.styleColorReview.flowType;
};
const getSubheader: Selector<AppState, SubheaderSlice> = (state) => {
  return state.subheader;
};
const getSearchIndexes: Selector<AppState, string[] | undefined> = (state) => {
  return state.pages.hindsighting.styleColorReview.flowType.viewDefn.searchIndexes;
};
const getWorklist: Selector<AppState, WorklistInfo[]> = (state) => {
  return state.pages.hindsighting.styleColorReview.sharedData.worklist;
};

export const getProcessedData = createSelector(
  getTreeData,
  getView,
  getSubheader,
  getWorklist,
  (treeData, viewSlice, subheader, worklist) => {
    if (treeData && viewSlice && viewSlice.groupingInfo && subheader) {
      const groupingInfo = viewSlice.groupingInfo;
      const groupBy = generateGroupBy(groupingInfo.dataIndex);
      const dataInputs: CardViewDataInputs = {
        styles: treeData,
        subheader: { ...subheader, groupBy },
        defns: {
          view: viewSlice.viewDefn || {},
          rollup: viewSlice.calcViewDefn || {},
        },
      };
      const optionalInputs: CardViewOptionalInputs = {
        worklist,
        staticColumns: groupingInfo.staticColumns,
      };

      return processDataForCardView(dataInputs, optionalInputs);
    }
    return [];
  },
);

export const getSummary = createSelector(
  getView,
  getTreeData,
  getRollup,
  getSubheader,
  getSearchIndexes,
  (viewSlice, treeData, rollupConfig, subheader, searchIndexes) => {
    const views = rollupConfig.view;
    const searchFields = searchIndexes ?? externalGridSearchFields;
    const groupDataIndex = getGroupBySelectedOptionProperty(subheader.groupBy, 'dataIndex');
    const groupDimension = getGroupBySelectedOptionProperty(subheader.groupBy, 'dimension');
    const flat = flattenAndLabelGroup({ items: treeData, groupDataIndex, groupDimension });
    if (isNil(flat) || flat.length === 0 || !Array.isArray(views) || views.length === 0) {
      return [] as GetSummaryOutput[];
    }
    const filteredFlat = filterData(
      flat,
      subheader.search,
      searchFields,
      subheader.flowStatus,
      subheader.pareDown && subheader.pareDown.selections,
    );
    return toSummaries(filteredFlat, views, viewSlice.identityProps.idProp);
  },
);
